import { useMemo, useState } from 'react';
import { Pill, type PillVariant } from '@jywer/ui';
import { updateUser } from './api';
import type { UserRole, UserRow } from './types';

const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Admin',
  sales: 'Sælger',
  support: 'Support',
  pm: 'Project manager',
  viewer: 'Viewer',
};

const ROLE_VARIANT: Record<UserRole, PillVariant> = {
  admin: 'danger',
  sales: 'info',
  support: 'neutral',
  pm: 'success',
  viewer: 'muted',
};

/**
 * UI preset buttons must match the canonical RBAC presets in
 * `packages/auth/src/permission-presets.ts`. Drift means clicking a
 * preset grants an incomplete set — Daniel hit this on 2026-05-11
 * (SEO-specialist preset was missing `edit:specialist.seo`, so the
 * granted user couldn't edit pillars).
 *
 * If you add a new RBAC preset, mirror it here AND in PRESET_LABEL.
 */
const PRESETS: Record<string, readonly string[]> = {
  partner: [
    'view:main.projektoverblik.websites',
    'view:main.projektoverblik.video',
    'view:main.clients',
    'view:main.dashboard.personal',
    'view:admin.billing',
    'view:admin.commissions',
    'view:admin.rates',
    'view:admin.clients',
    'view:admin.revenue-pipeline',
    'view:admin.orders',
    'edit:admin.orders',
    'edit:admin.orders.contract',
    'view:admin.subscriptions',
    'edit:admin.subscriptions',
    'edit:project.tasks',
  ],
  sales: [
    'view:main.projektoverblik.websites',
    'view:main.projektoverblik.video',
    'view:main.clients',
    'view:main.dashboard.personal',
    'edit:project.tasks',
  ],
  specialist_seo: ['view:specialist.seo', 'edit:specialist.seo', 'view:main.clients'],
  support_specialist: ['view:main.support-tasks', 'view:main.clients'],
  billing_reviewer: ['view:admin.billing', 'view:admin.rates', 'view:admin.commissions'],
};

const PRESET_LABEL: Record<keyof typeof PRESETS, string> = {
  partner: 'Partner',
  sales: 'Sælger',
  specialist_seo: 'SEO-specialist',
  support_specialist: 'Support-specialist',
  billing_reviewer: 'Billing reviewer',
};

export function UsersTab({
  users,
  viewIds,
  onUsersChange,
}: {
  users: UserRow[];
  viewIds: readonly string[];
  onUsersChange: (users: UserRow[]) => void;
}) {
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return users
      .filter((u) => (showInactive ? true : u.active))
      .filter((u) =>
        s
          ? u.display_name.toLowerCase().includes(s) ||
            u.email.toLowerCase().includes(s) ||
            (u.department ?? '').toLowerCase().includes(s)
          : true,
      )
      .sort((a, b) => a.display_name.localeCompare(b.display_name, 'da'));
  }, [users, search, showInactive]);

  async function handleToggleActive(u: UserRow, nextActive: boolean) {
    await updateUser(u.id, { active: nextActive });
    onUsersChange(users.map((x) => (x.id === u.id ? { ...x, active: nextActive } : x)));
  }

  async function handleSaveDrawer(patch: {
    display_name: string;
    role: UserRole;
    department: string | null;
    active: boolean;
    permissions: string[];
  }) {
    if (!editing) return;
    await updateUser(editing.id, patch);
    onUsersChange(users.map((x) => (x.id === editing.id ? { ...x, ...patch } : x)));
    setEditing(null);
  }

  return (
    <div>
      <p className="mb-4 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
        Adgang til <code>admin.jywer.*</code> administreres i Cloudflare Access dashboardet, ikke
        her. Denne side styrer kun dataattribution (sales_rep_id, godkender-felter osv.).
      </p>

      <div className="mb-3 flex items-center gap-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Søg efter navn, email, department…"
          className="w-64 rounded border border-line-2 px-2 py-1 text-sm"
        />
        <label className="flex items-center gap-2 text-sm text-ink-1">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Vis inaktive
        </label>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line-2 text-left text-xs uppercase text-ink-2">
            <th className="py-2">Navn</th>
            <th>Email</th>
            <th>Rolle</th>
            <th>Department</th>
            <th className="text-right">Aktive tokens</th>
            <th className="text-right">Aktiv</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((u) => (
            <tr
              key={u.id}
              className="cursor-pointer border-b border-line-1 hover:bg-paper-1"
              onClick={() => setEditing(u)}
            >
              <td className="py-2 font-medium">{u.display_name}</td>
              <td className="text-ink-1">{u.email}</td>
              <td>
                <Pill variant={ROLE_VARIANT[u.role]}>{ROLE_LABEL[u.role]}</Pill>
              </td>
              <td>{u.department ?? '—'}</td>
              <td className="text-right">{u.active_service_token_count}</td>
              <td className="text-right">
                <input
                  type="checkbox"
                  checked={u.active}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => void handleToggleActive(u, e.target.checked)}
                />
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr>
              <td colSpan={6} className="py-6 text-center text-ink-2">
                Ingen brugere matcher filteret.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {editing && (
        <EditUserDrawer
          user={editing}
          viewIds={viewIds}
          onClose={() => setEditing(null)}
          onSave={handleSaveDrawer}
        />
      )}
    </div>
  );
}

function EditUserDrawer({
  user,
  viewIds,
  onClose,
  onSave,
}: {
  user: UserRow;
  viewIds: readonly string[];
  onClose: () => void;
  onSave: (patch: {
    display_name: string;
    role: UserRole;
    department: string | null;
    active: boolean;
    permissions: string[];
  }) => Promise<void>;
}) {
  const [displayName, setDisplayName] = useState(user.display_name);
  const [role, setRole] = useState<UserRole>(user.role);
  const [department, setDepartment] = useState(user.department ?? '');
  const [active, setActive] = useState(user.active);
  const [permissions, setPermissions] = useState<string[]>([...user.permissions]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isSuperadmin = permissions.includes('*');

  function togglePermission(id: string, on: boolean) {
    if (isSuperadmin) return; // superadmin replaces view checkboxes
    setPermissions((prev) =>
      on ? Array.from(new Set([...prev, id])) : prev.filter((p) => p !== id),
    );
  }

  function applyPreset(key: keyof typeof PRESETS) {
    const preset = PRESETS[key];
    setPermissions(preset ? [...preset] : []);
  }

  function setSuperadmin(on: boolean) {
    setPermissions(on ? ['*'] : []);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      await onSave({
        display_name: displayName,
        role,
        department: department.trim() || null,
        active,
        permissions,
      });
    } catch (x) {
      setErr(x instanceof Error ? x.message : 'Kunne ikke gemme');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex justify-end bg-black/30" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => void handleSubmit(e)}
        className="w-[420px] overflow-y-auto bg-paper-0 p-6 shadow-xl"
      >
        <h3 className="mb-4 text-lg font-semibold text-primary">Rediger bruger</h3>
        <label className="mb-3 block text-sm">
          <span className="mb-1 block text-ink-2">Navn</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full rounded border border-line-2 px-2 py-1"
            required
          />
        </label>
        <label className="mb-3 block text-sm">
          <span className="mb-1 block text-ink-2">Email</span>
          <input
            type="email"
            value={user.email}
            disabled
            className="w-full rounded border border-line-1 bg-paper-1 px-2 py-1 text-ink-2"
          />
        </label>
        <label className="mb-3 block text-sm">
          <span className="mb-1 block text-ink-2">Rolle</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
            className="w-full rounded border border-line-2 px-2 py-1"
          >
            {(Object.keys(ROLE_LABEL) as UserRole[]).map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </label>
        <label className="mb-3 block text-sm">
          <span className="mb-1 block text-ink-2">Department</span>
          {/* PR #8 — locked-set dropdown per BUILD_PLAN decision #11.
              Backed by `users.department` CHECK constraint (matching
              values). Empty = no department. */}
          <select
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            className="w-full rounded border border-line-2 bg-paper-0 px-2 py-1"
          >
            <option value="">— ingen —</option>
            <option value="websites">Websites</option>
            <option value="video">Video</option>
            <option value="seo">SEO</option>
            <option value="support">Support</option>
            <option value="admin">Admin</option>
            <option value="sales">Sales</option>
          </select>
        </label>
        <label className="mb-4 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Aktiv
        </label>

        <div className="mb-4 border-t border-line-1 pt-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-ink-1">Adgang (RBAC)</span>
            <label className="flex items-center gap-2 text-xs text-status-err">
              <input
                type="checkbox"
                checked={isSuperadmin}
                onChange={(e) => setSuperadmin(e.target.checked)}
              />
              Superadmin (alt)
            </label>
          </div>
          <div className="mb-2 flex flex-wrap gap-1">
            {(Object.keys(PRESETS) as (keyof typeof PRESETS)[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => applyPreset(k)}
                disabled={isSuperadmin}
                className="rounded border border-line-2 px-2 py-0.5 text-xs text-ink-1 hover:border-accent hover:text-accent disabled:opacity-40"
              >
                {PRESET_LABEL[k]}
              </button>
            ))}
          </div>
          <div className={`grid grid-cols-1 gap-1 text-xs ${isSuperadmin ? 'opacity-50' : ''}`}>
            {viewIds.map((id) => (
              <label key={id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  disabled={isSuperadmin}
                  checked={isSuperadmin || permissions.includes(id)}
                  onChange={(e) => togglePermission(id, e.target.checked)}
                />
                <code className="text-ink-1">{id}</code>
              </label>
            ))}
          </div>
        </div>

        {err && <p className="mb-3 text-sm text-red-700">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded border px-3 py-1 text-sm">
            Annullér
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded bg-primary px-3 py-1 text-sm text-white disabled:opacity-60"
          >
            {saving ? 'Gemmer…' : 'Gem'}
          </button>
        </div>
      </form>
    </div>
  );
}
